import React from 'react';
import { compose, withHandlers, withProps, withState } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withMedia } from 'react-media-query-hoc';
import { connect } from 'react-redux';
import { selectors as warningsSelectors } from 'state/warnings';
import { selectors as errorsSelectors } from 'state/errors';
import { actions as addressesActions } from 'state/addresses';
import keyGenerator from 'utils/keys';
import { selectors as shipmentSelectors } from 'state/shipments';
import { isEmpty } from 'utils/types';

import StopAddress from './StopAddress';
import { selectors as orderSelectors } from 'state/order';

const enhance = compose(
  withTranslation('StopAddress'),
  withMedia,
  withState('focused', 'setFocused', false),
  connect(
    (state, { stopType, shipmentKey }) => ({
      address: shipmentSelectors.getShipmentAddress(
        state,
        shipmentKey,
        stopType
      ),
      rawAddress: shipmentSelectors.getShipmentRawAddress(
        state,
        shipmentKey,
        stopType
      ),
      warnings: warningsSelectors.getWarnings(state),
      errors: errorsSelectors.getErrors(state),
      orderType: orderSelectors.getTypeOfOrder(state),
    }),
    {
      searchAddress: addressesActions.searchAddresses,
      blurAddress: addressesActions.blurAddresses,
      cleanAddress: addressesActions.cleanAddress,
    }
  ),
  withHandlers(() => {
    let modalRef = React.createRef();
    let inputRef = React.createRef();
    let inputModalRef = React.createRef();
    return {
      closeModal:
        ({ setFocused, shipmentKey, stopType, blurAddress }) =>
        () => {
          if (modalRef && modalRef.close) modalRef.close();
          setFocused(false);
          blurAddress({ shipmentKey, stopType });
        },
      handleInputChange:
        ({ shipmentKey, stopType, searchAddress }) =>
        (_, { value }) => {
          searchAddress({ shipmentKey, stopType, text: value });
        },
      handleInputFocus:
        ({ setFocused }) =>
        () => {
          if (!inputRef || isEmpty(inputRef)) return;

          setFocused(true);
          inputRef.focus();

          setTimeout(() => {
            inputRef.setSelectionRange(0, inputRef.value.length);
          }, 0);
        },
      handleInputModalFocus:
        ({ setFocused }) =>
        () => {
          if (!inputModalRef || isEmpty(inputModalRef)) return;

          setFocused(true);
          inputModalRef.focus();
          // Workaround to solve this issue: https://bugs.chromium.org/p/chromium/issues/detail?id=32865
          setTimeout(() => {
            inputModalRef.setSelectionRange(0, inputModalRef.value.length);
          }, 0);
        },
      handleInputBlur:
        ({ setFocused, blurAddress, shipmentKey, stopType }) =>
        () => {
          setFocused(false);
          blurAddress({ shipmentKey, stopType });
        },
      responsiveModalRef: () => (ref) => {
        modalRef = ref;
      },
      inputRef: () => (ref) => {
        inputRef = ref;
      },
      inputModalRef: () => (ref) => {
        inputModalRef = ref;
      },
      handleClear:
        ({ cleanAddress, shipmentKey, stopType }) =>
        (e) => {
          cleanAddress({ shipmentKey, stopType });
          e.stopPropagation();
        },
    };
  }),
  withProps(
    ({ shipmentKey, stopType, focused, t, rawAddress, errors, warnings }) => ({
      t,
      focused,
      inputValue: rawAddress,
      id: keyGenerator.address[stopType]({ shipmentKey }),
      error:
        errors[keyGenerator.address.zipcode({ shipmentKey, stopType })] ||
        errors[keyGenerator.address[stopType]({ shipmentKey })],
      warning:
        warnings[keyGenerator.address.zipcode({ shipmentKey, stopType })] ||
        warnings[keyGenerator.address[stopType]({ shipmentKey })],
    })
  )
);

export default enhance(StopAddress);
